import * as React from 'react';

import { IntlMessage, useLocale } from 'app/components/Intl';
import { isEqual, isInteger, isNil, isNumeric, isString } from 'app/utils';
import formatter from 'app/utils/formatter';
import type { NumericInputHelperOptions } from 'app/components/FormikField/Text/NumericInputHelper';

import type { RegulationsField, RegulationsFieldOptions } from './RegulationsField.types';
import { RegulationsTableField } from './RegulationsTableField';

export type RegulationsFieldDisplayProps = {
  field: RegulationsField;
};

const resolveSelectLabel = (field: RegulationsField) => {
  const items = field.items || [];
  const selected = items.find(item => isEqual(item.value, field.value));

  if (selected) {
    return isString(selected.element) ? (
      <IntlMessage value={selected.element} />
    ) : (
      <IntlMessage value={`${selected.value}`} />
    );
  }

  return <IntlMessage value={field?.value} />;
};

const renderNumericValue = (value: any, options?: RegulationsFieldOptions) => {
  if (isNil(value) || value === '' || !isNumeric(value)) {
    return <>{'-'}</>;
  }
  if (options?.style === 'currency') {
    return <>{formatter.currency(value, options as NumericInputHelperOptions)}</>;
  }
  if (options?.style === 'percent') {
    return <>{formatter.percentage(value, options as NumericInputHelperOptions)}</>;
  }
  return <>{value}</>;
};

const renderDayAndMonth = (value: any) => {
  if (!value) {
    return <>{'-'}</>;
  }
  const date = new Date(Date.parse(value));
  return <>{`${date.getDate()}/${date.getMonth() + 1}`}</>;
};

const renderTextValue = (field: RegulationsField) => {
  if (field.options?.style === 'select') {
    return resolveSelectLabel(field);
  }
  return field.value ? <IntlMessage value={field.value} /> : <>{'-'}</>;
};

export const RegulationsFieldDisplay = ({ field }: RegulationsFieldDisplayProps) => {
  const { locale } = useLocale();

  if (React.isValidElement(field)) {
    return field;
  }

  const { type = 'text', value } = field;

  if (type === 'boolean' || type === 'checkbox') {
    return <>{formatter.yesNo(value)}</>;
  }

  switch (type) {
    case 'month':
      return isInteger(value) ? <>{locale?.localize?.month(Number.parseInt(value))}</> : <>{'-'}</>;

    case 'date':
      return value ? <>{formatter.date(value)}</> : <>{'-'}</>;

    case 'dayAndMonth':
      return renderDayAndMonth(value);

    case 'number':
    case 'float':
    case 'currency':
      return renderNumericValue(value, field.options);

    case 'select':
    case 'list':
    case 'autocomplete':
      return resolveSelectLabel(field);

    case 'table':
      return <RegulationsTableField field={field} isEditing={false} />;

    case 'text':
      return renderTextValue(field);

    default:
      return <IntlMessage value={value} />;
  }
};
